import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { getProductByID } from "../../utils/helper";
import Pagination from "../../components/UIElements/Pagination";
import { CHARGE_TYPES, PAYMENT_TERMS } from "../../constants/product";
import { getRFQ } from "../../redux/api/RFQs";

function Pair({ title, value, ext, className }) {
  return (
    <div className={`flex flex-col items-start gap-2 ${className || ""}`}>
      <h3 className="text-sm text-gray-400">{title}</h3>
      <p className="text-md">
        {value} {ext ? " " + ext : ""}
      </p>
    </div>
  );
}

function Card({
  id,
  rfqId,
  createdAt,
  quantity,
  status,
  tacType,
  unitPrice,
  gstCharges,
  qRemark,
  tCharges,
}) {
  const [RFQ, setRFQ] = useState({});

  useEffect(() => {
    if (!rfqId) return;
    getRFQ(rfqId).then(setRFQ);
  }, [rfqId]);

  const product = useMemo(() => {
    if (!RFQ.pId) return {};
    return getProductByID(RFQ.pId);
  }, [RFQ]);

  return (
    <div
      className={`bg-white rounded-xl grid grid-cols-2 gap-4 w-full p-5 font-medium rtl:text-right text-gray-500 border border-gray-200 hover:shadow`}
    >
      <Pair title="Product" value={product.title} />
      <Pair title="Unit" value={product.unit} />
      <div className="border-t col-span-2" />
      <Pair title="Quantity" value={quantity} />
      <Pair title="Unit Price" value={unitPrice} />
      <Pair
        title="GST Charges"
        value={gstCharges.value}
        ext={`(${CHARGE_TYPES[gstCharges.type]})`}
      />
      <Pair
        title="Transportation Charges"
        value={tCharges.value}
        ext={`(${CHARGE_TYPES[tCharges.type]})`}
      />
      <Pair title="Status" value={status ? "Accepted" : "Pending"} />
      <Pair
        title="Created At"
        value={createdAt.toDate().toLocaleDateString()}
      />
      <Pair title="Payment Terms" value={PAYMENT_TERMS[tacType]} />
      <Pair title="Quality Remark" value={qRemark} />
    </div>
  );
}

const PAGE_ITEM_COUNT = 5;
const FILTERS = ["all", "pending", "accepted"];

export default function Quotes() {
  const { userQuotes } = useSelector((s) => s.quotes);
  const [filter, setFilter] = useState(FILTERS[0]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const quotes = userQuotes.filter((quote) => {
      if (filter === "pending") {
        return quote.status === 0;
      } else if (filter === "accepted") {
        return quote.status === 1;
      }
      return true;
    });
    setFilteredQuotes([...quotes]);
  }, [filter, userQuotes]);

  return (
    <div className="h-full bg-gray-50">
      <div className="flex flex-wrap items-center gap-4 bg-white py-2 px-3">
        {FILTERS.map((fName) => {
          return (
            <button
              key={fName}
              onClick={() => setFilter(fName)}
              className={`capitalize inline-flex whitespace-nowrap border-b-2 border-transparent py-2 px-3 text-sm transition-all duration-200 ease-in-out ${
                filter === fName
                  ? "text-[#1D523B] border-b-[#1D523B] font-semibold"
                  : "text-gray-600 hover:border-b-[#1D523B] hover:text-[#1D523B]"
              }`}
            >
              {fName}
            </button>
          );
        })}
      </div>
      <div className="p-8 grid grid-cols-2 gap-4">
        {filteredQuotes.length ? (
          filteredQuotes
            .slice(
              (currentPage - 1) * PAGE_ITEM_COUNT,
              currentPage * PAGE_ITEM_COUNT
            )
            .map((quote) => {
              return <Card key={quote.id} {...quote} />;
            })
        ) : (
          <h4 className="text-xl">No Quotes Found.</h4>
        )}
      </div>
      {!!filteredQuotes.length && filteredQuotes.length > PAGE_ITEM_COUNT && (
        <div className="p-8">
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={filteredQuotes.length / PAGE_ITEM_COUNT}
          />
        </div>
      )}
    </div>
  );
}
